import { z } from '@customerio/hydra-validation';
import type { ReportCategoryColumn, ReportColumn, ReportMetricColumn } from './report-table';

/**
 * How a report's result is drawn: the chart type, the column along the x
 * axis, the metric columns drawn as series, and optionally a second category
 * whose values split the first series into one series each. The agent writes
 * one into a chat answer and a saved report stores one, so both are read
 * through this one schema. The chart has no title of its own: on a report the
 * page header names it, and in chat the answer around it does.
 */

export const REPORT_CHART_TYPES = ['line', 'bar', 'donut'] as const;

export const ReportChartMappingSchema = z.object({
  type: z.enum(REPORT_CHART_TYPES),
  x: z.string().min(1),
  series: z.array(z.object({ column: z.string().min(1), label: z.string().optional() })).min(1),
  pivot: z.string().min(1).optional(),
});

export type ReportChartMapping = z.output<typeof ReportChartMappingSchema>;

export type ReportChartType = ReportChartMapping['type'];

/** A stored or agent-written chart as a mapping, or null when it is not one. */
export function parseReportChartMapping(chart: unknown): ReportChartMapping | null {
  const parsed = ReportChartMappingSchema.safeParse(chart);
  return parsed.success ? parsed.data : null;
}

/** One series as the chart draws it: a metric column, and what to call it. */
export type ResolvedReportSeries = { column: ReportMetricColumn; label: string | null };

/** A mapping whose names have all been found among the result's columns. */
export type ResolvedReportMapping = {
  type: ReportChartType;
  x: ReportCategoryColumn;
  series: ResolvedReportSeries[];
  pivot: ReportCategoryColumn | null;
};

function categoryColumns(columns: readonly ReportColumn[]): ReportCategoryColumn[] {
  return columns.flatMap((column) => (column.role === 'metric' ? [] : [column]));
}

function metricColumns(columns: readonly ReportColumn[]): ReportMetricColumn[] {
  return columns.flatMap((column) => (column.role === 'metric' ? [column] : []));
}

/**
 * The mapping a result is drawn with when nothing says otherwise: the time
 * bucket along x, or else the first category, with every metric as a series.
 * A second category splits the first metric into a series per value. A time
 * bucket draws as a line, anything else as bars. Null when the result has
 * nothing to lay out or nothing to draw.
 */
export function defaultReportChartMapping(columns: readonly ReportColumn[]): ReportChartMapping | null {
  const categories = categoryColumns(columns);
  const metrics = metricColumns(columns);
  const x = categories.find((column) => column.role === 'time') ?? categories[0];
  const [firstMetric] = metrics;
  if (x == null || firstMetric == null) return null;

  const type = x.role === 'time' ? 'line' : 'bar';
  const pivot = categories.find((column) => column !== x);
  if (pivot == null) {
    return { type, x: x.name, series: metrics.map((column) => ({ column: column.name })) };
  }
  return { type, x: x.name, series: [{ column: firstMetric.name }], pivot: pivot.name };
}

/** The mapping's names found among the columns, or null when one of them isn't there. */
function resolveNames(columns: readonly ReportColumn[], mapping: ReportChartMapping): ResolvedReportMapping | null {
  const categories = categoryColumns(columns);
  const metrics = metricColumns(columns);
  const categoryNamed = (name: string) => categories.find((column) => column.name === name.toLowerCase());
  const x = categoryNamed(mapping.x);
  const pivot = mapping.pivot == null ? null : categoryNamed(mapping.pivot);
  const series = mapping.series.flatMap(({ column, label }) => {
    const found = metrics.find((candidate) => candidate.name === column.toLowerCase());
    return found == null ? [] : [{ column: found, label: label ?? null }];
  });
  if (x == null || pivot === undefined || pivot === x || series.length !== mapping.series.length) return null;
  return { type: mapping.type, x, series, pivot };
}

/**
 * The mapping to draw with: the one given, where every column it names is in
 * the result, else the default. A mapping written for an earlier version of
 * the query must still draw something rather than nothing.
 */
export function resolveReportChartMapping(
  columns: readonly ReportColumn[],
  mapping: ReportChartMapping | null,
): ResolvedReportMapping | null {
  const given = mapping == null ? null : resolveNames(columns, mapping);
  if (given != null) return given;
  const fallback = defaultReportChartMapping(columns);
  return fallback == null ? null : resolveNames(columns, fallback);
}
